import React, { useState, useEffect } from "react";
import InfoGridSection from "../components/sections/InfoGridSection";
import SliderForHome from '../pages/SliderForHome';
import CourseUpdatesSlider from '../components/CourseUpdatesSlider';
import { useNavigate, useLocation } from "react-router-dom";
import { FaGraduationCap } from "react-icons/fa";

import {
  Award,
  Phone,
  BookOpen,
  Target,
  Brain,
  Zap
} from 'lucide-react';

const Home = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [announcements, setAnnouncements] = useState([
    { id: '1', text: "🎓 UPSC & CAPF Mentorship Programme - New Batch Starting Soon!", link: "capf" },
    { id: '2', text: "📝 5 Months Answer Writing Intensive Batch - Admissions Open", link: "answer-writing" },
    { id: '3', text: "✍️ Essay & Ethics Special Course", link: "essay-writing" },
    { id: '4', text: "🏛️ UPSC Integrated Foundation Batch 2026-27", link: "upsc" },
    { id: '5', text: "🚩 MPSC Integrated Comprehensive Batch", link: "mpsc" },
  ]);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch course updates / announcements from WordPress CMS
  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const wpUrl = 'https://dimensionias.com/cms/wp-json/wp/v2';
        const catRes = await fetch(`${wpUrl}/categories?per_page=100`);
        if (catRes.ok) {
          const categories = await catRes.json();
          const updateCategory = categories.find(cat =>
            cat.slug === 'course-updates' || cat.slug === 'announcements' || cat.slug === 'updates' || cat.name.toLowerCase().includes('update')
          );
          if (updateCategory) {
            const postsRes = await fetch(`${wpUrl}/posts?categories=${updateCategory.id}&per_page=10&status=publish`);
            if (postsRes.ok) {
              const postsData = await postsRes.json();
              if (postsData.length > 0) {
                const wpAnnouncements = postsData.map(post => ({
                  id: post.id,
                  text: post.title.rendered.replace(/<[^>]*>/g, ''),
                  link: 'courses'
                }));
                setAnnouncements(wpAnnouncements);
              }
            }
          }
        }
      } catch (err) {
        console.log('Using default course updates ticker');
      }
    };

    fetchAnnouncements();
  }, []);

  // Scroll to top when component mounts or location changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleCourseNavigation = (courseId) => {
    if (location.pathname === '/courses') {
      const element = document.querySelector(`#${courseId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate(`/courses#${courseId}`);
    }
  };

  React.useEffect(() => {
    const animateCounters = () => {
      const duration = 2000;
      const steps = 50;
      const stepDuration = duration / steps;
      let currentStep = 0;
      const timer = setInterval(() => {
        currentStep++;
        if (currentStep >= steps) {
          clearInterval(timer);
        }
      }, stepDuration);
    };
    const timer = setTimeout(animateCounters, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Updated courses with attractive colors for tabs and borders
  const courses = [
    {
      title: "UPSC",
      subtitle: "Union Public Service Commission",
      tabColor: "#16A085", // Attractive teal-green
      borderColor: "#16A085",
      features: [
        { name: "UPSC Foundation Course", id: "upsc-foundation" },
        { name: "Integrated Batch (All Stages)", id: "upsc-integrated" },
        { name: "Personalized Mentorship Programme", id: "personalized-mentorship" },
        { name: "5-Month Answer Writing Programme", id: "answer-writing" },
        { name: "Optional Subjects", id: "optional-subjects" },
        { name: "Essay Writing Programme", id: "essay-writing" },
        { name: "Ethics Course", id: "ethics-plus" },
      ],
    },
    {
      title: "MPSC",
      subtitle: "Maharashtra Public Service Commission",
      tabColor: "#2980B9", // Attractive royal blue
      borderColor: "#2980B9",
      features: [
        { name: "MPSC Foundation Course", id: "mpsc-foundation" },
        { name: "Integrated Batch – Prelims | Mains | Interview", id: "mpsc-integrated" },
        { name: "वैयक्तिक मार्गदर्शन कार्यक्रम", id: "mpsc-personalized-mentorship" },
        { name: "5-Month Answer Writing (MPSC)", id: "mpsc-answer-writing" },
        { name: "निबंध लेखन प्रशिक्षण कार्यक्रम", id: "mpsc-essay" },
        { name: "ETHICS कोर्स - MPSC साठी विशेष", id: "ethics-mentorship" },
      ],
    },
    {
      title: "CAPF",
      subtitle: "Central Armed Police Forces",
      tabColor: "#E67E22", // Attractive orange
      borderColor: "#E67E22",
      features: [
        { name: "CAPF Mentorship Programme", id: "capf-mentorship" },
      ],
    },
  ];

  const CourseCard = ({ title, subtitle, tabColor, borderColor, features }) => (
    <div
      className="rounded-2xl shadow-lg p-4 sm:p-5 w-full bg-white flex flex-col justify-between hover:shadow-xl transform hover:-translate-y-1.5 transition-all duration-300 border-2"
      style={{ borderColor: borderColor }}
    >
      <div>
        {/* Colored Tab for Course Title */}
        <div
          className="inline-flex items-center gap-2 mb-2 px-3 py-1.5 rounded-xl font-bold text-base sm:text-lg"
          style={{ backgroundColor: tabColor, color: 'white' }}
        >
          <FaGraduationCap size={20} />
          <span>{title}</span>
        </div>

        <div className="mb-2 text-xs sm:text-sm font-bold text-gray-800">{subtitle}</div>
        <div className="flex-1">
          <ul className="space-y-1">
            {features.filter(f => f.name && f.name !== '.').map((feature) => (
              <li
                key={feature.id}
                onClick={() => handleCourseNavigation(feature.id)}
                className="cursor-pointer hover:bg-emerald-50 transition-all duration-150 p-1.5 px-2.5 rounded-md flex items-center group text-gray-800 hover:text-[#134E4A] font-semibold text-xs"
              >
                <span className="leading-snug font-bold">• {feature.name}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-gray-200">
        <button
          onClick={() => handleCourseNavigation(title.toLowerCase())}
          className="w-full font-bold py-2 px-3 rounded-lg transition-all duration-200 hover:scale-105 text-white text-xs"
          style={{
            backgroundColor: tabColor,
            border: 'none',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = '#134E4A';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = tabColor;
          }}
        >
          View All {title} Courses →
        </button>
      </div>
    </div>
  );

  return (
    <div className="overflow-hidden" style={{ backgroundColor: '#F0FDF7' }}>
      {/* Navbar height spacer & Top Course Announcement Ticker */}
      <div className="pt-20 md:pt-24" id="top">
        <div className="w-full bg-teal-900 text-teal-100 py-1.5 shadow-inner overflow-hidden border-y border-teal-700/50 flex items-center">
          <div
            className={`inline-block font-semibold text-xs sm:text-sm whitespace-nowrap animate-marquee w-full ${isHovered ? 'paused' : ''}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {announcements.map((item, idx) => (
              <React.Fragment key={item.id || idx}>
                <span
                  onClick={() => handleCourseNavigation(item.link || "courses")}
                  className="cursor-pointer hover:text-teal-300 hover:underline transition-colors duration-200 ml-4"
                >
                  📢 {item.text}
                </span>
                {idx < announcements.length - 1 && (
                  <span className="mx-4 text-teal-400">✨</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Course Banner Image Slider (10:1 Ratio) */}
      <CourseUpdatesSlider />

      {/* Hero Section & Slider */}
      <div className="mt-0">
        <SliderForHome />
      </div>

      {/* Courses Section */}
      <section className="py-6 sm:py-8" style={{ backgroundColor: '#E5E7EB' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-4 sm:mb-6">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#134E4A] mb-1">
              Our Courses
            </h2>
            <p className="text-xs sm:text-sm font-semibold text-slate-600 max-w-2xl mx-auto">
              Comprehensive preparation programs designed for your success in competitive examinations
            </p>
          </div>

          {/* Course Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 items-stretch">
            {courses.map((course) => (
              <CourseCard
                key={course.title}
                title={course.title}
                subtitle={course.subtitle}
                tabColor={course.tabColor}
                borderColor={course.borderColor}
                features={course.features}
              />
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-6">
            <button
              onClick={() => navigate('/courses')}
              className="font-extrabold text-xs sm:text-sm py-2.5 px-6 rounded-full shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 text-white"
              style={{ background: '#2DD4BF' }}
              onMouseEnter={e => e.currentTarget.style.background = '#134E4A'}
              onMouseLeave={e => e.currentTarget.style.background = '#2DD4BF'}
            >
              Explore All Courses →
            </button>
          </div>
        </div>
      </section>

      {/* Info Grid Section */}
      <InfoGridSection />

      {/* Faculty Section */}
      <section className="py-6 sm:py-8" style={{ backgroundColor: '#F0FDF7' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4 sm:mb-6">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#134E4A] mb-1">
              Our Expert Faculty
            </h2>
            <p className="text-xs sm:text-sm font-semibold text-slate-600 max-w-2xl mx-auto">
              Learn from experienced educators and subject matter experts
            </p>
          </div>

          {/* Faculty Grid - Sleek 7 Column Single Row Layout */}
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 border-2" style={{ borderColor: '#E0F2F1' }}>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 text-center">
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden mb-2 border-2 border-[#2DD4BF] shadow-md group-hover:scale-105 transition-transform">
                  <img src="/images/mentors/sumant.jpg" alt="Sumant Kamble" className="w-full h-full object-cover object-[center_top]" />
                </div>
                <h3 className="text-xs sm:text-sm font-extrabold text-[#134E4A]">Sumant Kamble</h3>
                <p className="text-[10px] sm:text-xs text-slate-500 font-semibold">(Economy, IR, Ethics)</p>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden mb-2 border-2 border-[#2DD4BF] shadow-md group-hover:scale-105 transition-transform">
                  <img src="/images/mentors/ranjit.webp" alt="Ranjeet Thipe" className="w-full h-full object-cover object-[center_top]" />
                </div>
                <h3 className="text-xs sm:text-sm font-extrabold text-[#134E4A]">Ranjeet Thipe</h3>
                <p className="text-[10px] sm:text-xs text-slate-500 font-semibold">(Ethics, History)</p>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden mb-2 border-2 border-[#2DD4BF] shadow-md group-hover:scale-105 transition-transform">
                  <img src="/images/mentors/pallavi.webp" alt="Pallavi Sangle" className="w-full h-full object-cover object-[center_top]" />
                </div>
                <h3 className="text-xs sm:text-sm font-extrabold text-[#134E4A]">Pallavi Sangle</h3>
                <p className="text-[10px] sm:text-xs text-slate-500 font-semibold">(Essay, Anthro)</p>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden mb-2 border-2 border-[#2DD4BF] shadow-md group-hover:scale-105 transition-transform">
                  <img src="/images/mentors/abhijit.webp" alt="Abhijit Chaugule" className="w-full h-full object-cover object-[center_top]" />
                </div>
                <h3 className="text-xs sm:text-sm font-extrabold text-[#134E4A]">Abhijit Chaugule</h3>
                <p className="text-[10px] sm:text-xs text-slate-500 font-semibold">(Geography, Polity)</p>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden mb-2 border-2 border-[#2DD4BF] shadow-md group-hover:scale-105 transition-transform">
                  <img src="/images/mentors/aditya.webp" alt="Aditya Kedari" className="w-full h-full object-cover object-[center_top]" />
                </div>
                <h3 className="text-xs sm:text-sm font-extrabold text-[#134E4A]">Aditya Kedari</h3>
                <p className="text-[10px] sm:text-xs text-slate-500 font-semibold">(Sociology)</p>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden mb-2 border-2 border-[#2DD4BF] shadow-md group-hover:scale-105 transition-transform">
                  <img src="/images/mentors/payal.webp" alt="Payal Muzumdar" className="w-full h-full object-cover object-[center_top]" />
                </div>
                <h3 className="text-xs sm:text-sm font-extrabold text-[#134E4A]">Payal Muzumdar</h3>
                <p className="text-[10px] sm:text-xs text-slate-500 font-semibold">(Environment)</p>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden mb-2 border-2 border-[#2DD4BF] shadow-md group-hover:scale-105 transition-transform">
                  <img src="/images/mentors/swapnil.webp" alt="Swapnil Kshirsagar" className="w-full h-full object-cover object-[center_top]" />
                </div>
                <h3 className="text-xs sm:text-sm font-extrabold text-[#134E4A]">Swapnil Kshirsagar</h3>
                <p className="text-[10px] sm:text-xs text-slate-500 font-semibold">(Science & Tech)</p>
              </div>
            </div>

            {/* CTA for Faculty */}
            <div className="text-center mt-4">
              <button
                onClick={() => navigate('/about-us#faculty')}
                className="font-bold text-xs py-2 px-5 rounded-full shadow hover:shadow-md transition-all duration-300 text-white"
                style={{ background: '#2DD4BF' }}
                onMouseEnter={e => e.currentTarget.style.background = '#134E4A'}
                onMouseLeave={e => e.currentTarget.style.background = '#2DD4BF'}
              >
                Meet Complete Faculty Team →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section - Reduced Height by 35% */}
      <section className="py-6 sm:py-8" style={{ backgroundColor: '#F0FDF7' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4 sm:mb-5">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#134E4A] mb-1">
              Why Choose <span className="text-[#2DD4BF]">DimensionIAS?</span>
            </h2>
            <p className="text-xs sm:text-sm font-semibold text-slate-600 max-w-xl mx-auto">
              Experience excellence in UPSC preparation with our comprehensive platform
            </p>
          </div>

          {/* 6 Cards Compact 3-Column Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3.5">
            {[
              {
                icon: <Target size={20} color="white" />,
                title: "Personalized Study Plan",
                desc: "Customized learning paths designed according to your strengths and goals",
                bg: "#134E4A"
              },
              {
                icon: <Phone size={20} color="white" />,
                title: "1-on-1 Mentorship",
                desc: "Direct guidance from IAS officers and top rankers for personalized support",
                bg: "#134E4A"
              },
              {
                icon: <Brain size={20} color="white" />,
                title: "Smart Progress Tracking",
                desc: "Advanced analytics to monitor your preparation and identify improvement areas",
                bg: "#2DD4BF"
              },
              {
                icon: <Award size={20} color="white" />,
                title: "Proven Methodologies",
                desc: "Time-tested strategies and techniques from successful UPSC candidates",
                bg: "#134E4A"
              },
              {
                icon: <Zap size={20} color="white" />,
                title: "Interactive Learning",
                desc: "Engaging content with quizzes, discussions, and real-time feedback",
                bg: "#134E4A"
              },
              {
                icon: <BookOpen size={20} color="white" />,
                title: "Complete Study Material",
                desc: "Comprehensive resources covering all subjects and current affairs",
                bg: "#2DD4BF"
              }
            ].map((item, idx) => (
              <div
                key={idx}
                className="group p-3.5 sm:p-4 rounded-xl hover:shadow-md transition-all duration-300 border bg-white flex items-start gap-3 border-slate-200 hover:border-[#2DD4BF]"
              >
                <div className="inline-flex items-center justify-center w-9 h-9 rounded-lg shrink-0 shadow" style={{ backgroundColor: item.bg }}>
                  {item.icon}
                </div>
                <div>
                  <h3 className="text-xs sm:text-sm font-extrabold text-[#134E4A] mb-0.5 group-hover:text-[#2DD4BF] transition-colors">{item.title}</h3>
                  <p className="text-[11px] sm:text-xs text-slate-600 leading-snug">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-10 sm:py-12 text-white" style={{ background: 'linear-gradient(135deg, #134E4A, #2DD4BF)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-5xl font-bold mb-6">Ready to Begin Your Journey?</h3>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90 leading-relaxed">
            Join thousands of successful candidates who trusted DimensionIAS for their civil services preparation.
            Your success story starts here.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/courses")}
              className="px-8 py-4 rounded-full font-semibold text-lg hover:scale-105 transition-all duration-300 shadow-xl"
              style={{ backgroundColor: '#F0FDF7', color: '#134E4A' }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.color = '#2DD4BF';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = '#F0FDF7';
                e.currentTarget.style.color = '#134E4A';
              }}
            >
              Explore Courses
            </button>
            <button
              onClick={() => navigate("/contact")}
              className="border-2 px-8 py-4 rounded-full font-semibold text-lg hover:scale-105 transition-all duration-300 shadow-xl"
              style={{ borderColor: '#F0FDF7', color: '#F0FDF7', background: 'transparent' }}
              onMouseEnter={e => {
                e.currentTarget.style.background = '#F0FDF7';
                e.currentTarget.style.color = '#134E4A';
                e.currentTarget.style.borderColor = '#2DD4BF';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#F0FDF7';
                e.currentTarget.style.borderColor = '#F0FDF7';
              }}
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;